import { useState, useEffect, useCallback } from "react";
import {
    CheckCircle2,
    AlertCircle,
    Info,
    AlertTriangle,
    X,
} from "lucide-react";
import type { AlertProps } from "@/types/types";

export default function Alert({
    type,
    title,
    message,
    dismissible = true,
    onDismiss,
    autoClose = 4000,
    className = "",
}: AlertProps) {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);

    const handleDismiss = useCallback(() => {
        if (isExiting) return;
        setIsExiting(true);

        setTimeout(() => {
            setIsVisible(false);
            onDismiss?.();
        }, 380);
    }, [isExiting, onDismiss]);

    // Auto-close timer
    useEffect(() => {
        if (autoClose > 0 && isVisible && !isExiting) {
            const timer = setTimeout(handleDismiss, autoClose);
            return () => clearTimeout(timer);
        }
    }, [autoClose, isVisible, isExiting, handleDismiss]);

    if (!isVisible) return null;

    const styles = {
        success: {
            bg: "bg-emerald-50 dark:bg-emerald-900/70",
            border: "border-emerald-500/30",
            icon: <CheckCircle2 className="text-emerald-500" size={20} />,
            titleText: "text-emerald-800 dark:text-emerald-400",
            msgText: "text-emerald-600 dark:text-emerald-500/80",
            closeBtn:
                "text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-500/20",
            progress: "bg-emerald-400/60 dark:bg-emerald-500/40",
        },
        danger: {
            bg: "bg-red-50 dark:bg-red-900/50",
            border: "border-red-500/30",
            icon: <AlertCircle className="text-red-500" size={20} />,
            titleText: "text-red-800 dark:text-red-400",
            msgText: "text-red-600 dark:text-red-500/80",
            closeBtn: "text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20",
            progress: "bg-red-400/60 dark:bg-red-500/40",
        },
        warning: {
            bg: "bg-amber-50 dark:bg-amber-900/50",
            border: "border-amber-500/30",
            icon: <AlertTriangle className="text-amber-500" size={20} />,
            titleText: "text-amber-800 dark:text-amber-400",
            msgText: "text-amber-600 dark:text-amber-500/80",
            closeBtn:
                "text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-500/20",
            progress: "bg-amber-400/60 dark:bg-amber-500/40",
        },
        info: {
            bg: "bg-gold/10 dark:bg-gold/30",
            border: "border-gold/30 dark:border-gold/20",
            icon: <Info className="text-gold" size={20} />,
            titleText: "text-dark dark:text-gold",
            msgText: "text-dark/70 dark:text-white/60",
            closeBtn: "text-gold hover:bg-gold/20 dark:hover:bg-gold/10",
            progress: "bg-gold/50 dark:bg-gold/30",
        },
    };

    const s = styles[type];

    return (
        <div
            className={`fixed top-4 right-4 md:top-28 md:right-8 z-100 w-[90%] md:w-auto max-w-sm ${
                isExiting ? "alert-exit" : "alert-enter"
            }`}>
            {/* Card */}
            <div
                className={`relative overflow-hidden flex items-start gap-3 px-5 py-4
                            rounded-2xl border shadow-2xl
                            bg-white/95 dark:bg-dark-2/95 backdrop-blur-md
                            ${s.bg} ${s.border} ${className}`}>
                {/* Left accent bar */}
                <span
                    className={`absolute left-0 top-3 bottom-3 w-0.75 rounded-full ${s.progress}`}
                />

                {/* Icon with pop animation */}
                <div className="alert-icon-pop shrink-0 mt-0.5 ml-1">
                    {s.icon}
                </div>

                {/* Text */}
                <div className="flex-1 flex flex-col min-w-0">
                    <h4
                        className={`text-sm md:text-base font-bold leading-snug ${s.titleText}`}>
                        {title}
                    </h4>
                    {message && (
                        <p
                            className={`text-xs md:text-sm mt-0.5 leading-relaxed ${s.msgText}`}>
                            {message}
                        </p>
                    )}
                </div>

                {/* Close button */}
                {dismissible && (
                    <button
                        onClick={handleDismiss}
                        className={`shrink-0 p-1.5 rounded-lg transition-all duration-200
                                    cursor-pointer -mt-1 ml-1 -mr-1
                                    hover:scale-110 active:scale-95
                                    ${s.closeBtn}`}
                        aria-label="Dismiss">
                        <X size={15} />
                    </button>
                )}

                {/* Auto-close progress bar */}
                {autoClose > 0 && !isExiting && (
                    <span
                        className={`absolute bottom-0 left-0 h-[2.5px] rounded-full ${s.progress}`}
                        style={{
                            animation: `progressShrink ${autoClose}ms linear forwards`,
                        }}
                    />
                )}
            </div>
        </div>
    );
}
